import { ShoppingCart, UserRound } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink } from 'react-router-dom';

import { useCurrentUser } from '../lib/auth';
import { useCart } from '../lib/cart';
import { cn } from '../lib/cn';

import { LanguagePillSwitcher } from './LanguagePillSwitcher';
import { Logo } from './Logo';
import { Badge } from './ui/badge';
import { Button } from './ui/button';

export function Header() {
  const { itemCount } = useCart();
  const { user, loading } = useCurrentUser();
  const { t } = useTranslation('customer');

  return (
    <header className="sticky top-0 z-30 border-b border-border/60 bg-card/90 backdrop-blur shadow-sm">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-8 py-3">
        <Link to="/" className="flex items-center hover:opacity-90 transition-opacity">
          <Logo className="h-12 w-auto hidden sm:block" variant="small-with-tagline" />
          <Logo className="h-12 w-auto sm:hidden" variant="small" />
        </Link>
        <nav aria-label="Primary" className="hidden items-center gap-1 md:flex">
          <NavLink
            className={({ isActive }) =>
              cn(
                'px-4 py-2 text-sm font-medium transition-colors rounded-lg',
                isActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
              )
            }
            to="/"
          >
            {t('nav.discover')}
          </NavLink>
          <NavLink
            className={({ isActive }) =>
              cn(
                'px-4 py-2 text-sm font-medium transition-colors rounded-lg',
                isActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
              )
            }
            to="/account"
          >
            {t('nav.myOrders')}
          </NavLink>
        </nav>
        <div className="flex items-center gap-2">
          <LanguagePillSwitcher className="hidden sm:flex" />
          <Button asChild variant="ghost" className="relative rounded-xl hover:bg-primary/10">
            <Link to="/cart" aria-label={t('nav.cartLabel', { count: itemCount })}>
              <ShoppingCart className="h-5 w-5" />
              <span className="hidden sm:inline">{t('nav.cart')}</span>
              {itemCount > 0 ? (
                <Badge className="absolute -right-1 -top-1 h-5 min-w-5 justify-center px-1 text-[10px] bg-accent text-accent-foreground border-2 border-card shadow-md">
                  {itemCount}
                </Badge>
              ) : null}
            </Link>
          </Button>
          {loading ? null : user ? (
            <Button
              asChild
              variant="outline"
              size="icon"
              className="rounded-xl border-2 border-primary/30 hover:bg-primary/10 hover:border-primary"
            >
              <Link to="/account" aria-label={t('nav.account')}>
                <UserRound className="h-5 w-5" />
              </Link>
            </Button>
          ) : (
            <Button asChild>
              <Link to="/account/login">{t('nav.signIn')}</Link>
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
